import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import {
  PageHero,
  Reveal,
  FinalCTA,
  usePageMeta,
} from "../components/Common";
import { ARTICLES, ARTICLE_CATEGORIES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Insights = () => {
  usePageMeta(
    "Ideas, Questions & Possibilities — English for Leaders",
    "Exploring the future of learning, leadership, communication and technology.",
    "/insights"
  );

  // Published articles come from the API; static content is the offline fallback.
  const [articles, setArticles] = useState(ARTICLES.filter((a) => !a.draft));
  const [active, setActive] = useState("All");

  useEffect(() => {
    let live = true;
    axios
      .get(`${API}/articles`)
      .then((r) => {
        if (live && Array.isArray(r.data) && r.data.length) {
          setArticles(r.data.map((a) => ({ ...a, readTime: a.read_time })));
        }
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  // Only offer category filters that contain at least one published article.
  // Empty categories stay defined in ARTICLE_CATEGORIES for future content.
  const availableCategories = ARTICLE_CATEGORIES.filter((c) =>
    articles.some((a) => a.category === c)
  );
  const filters = ["All", ...availableCategories];
  const filtered =
    active === "All" ? articles : articles.filter((a) => a.category === active);

  return (
    <div data-testid="insights-page">
      <PageHero
        testid="insights-hero"
        eyebrow="The Editorial Hub"
        title="Ideas, Questions & Possibilities"
        intro="Exploring the future of learning, leadership, communication and technology — from curiosity and experience rather than from a sales brochure."
      />

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
          <Reveal>
            <div
              className="-mx-6 flex gap-3 overflow-x-auto px-6 pb-2 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0"
              role="group"
              aria-label="Filter articles by category"
              data-testid="category-filters"
            >
              {filters.map((f) => (
                <button
                  key={f}
                  type="button"
                  data-testid={`filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setActive(f)}
                  className={`shrink-0 rounded-sm border px-5 py-2.5 text-sm font-bold transition-colors duration-300 ${
                    active === f
                      ? "border-navy bg-navy text-white"
                      : "border-navy/20 bg-white text-navy hover:border-navy"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((a, i) => (
              <Reveal key={a.slug} delay={Math.min(i, 2) * 0.08}>
                <Link
                  to={`/insights/${a.slug}`}
                  data-testid={`article-card-${a.slug}`}
                  className="group relative flex h-full flex-col border border-navy/10 bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/5"
                >
                  <span className="block h-1 w-10 bg-crimson" aria-hidden="true" />
                  <div className="mt-5 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-navy/55">
                    <span>{a.category}</span>
                    <span className="text-charcoal/30" aria-hidden="true">·</span>
                    <span>{a.readTime}</span>
                  </div>
                  <h2 className="mt-3 font-display text-xl leading-snug text-navy">
                    {a.title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
                    {a.excerpt}
                  </p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-bold text-crimson">
                    Read the article
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
};

export default Insights;
